<template>
  <div class="home-page">
    <!-- Header Navigation -->
    <header class="header-nav" :class="{ 'scrolled': isScrolled }">
      <div class="nav-container">
        <div class="logo">
          <h2 class="logo-text" @click="gotoMain" style="cursor: pointer;">장기요양지원센터</h2>
        </div>
        
        <!-- Desktop Menu --> 
        <nav class="desktop-menu" v-show="$vuetify.display.lgAndUp">
          <v-btn 
            v-for="item in menuItems" 
            :key="item.title"
            text
            class="nav-btn"
            @click="scrollToSection(item.section)"
          >
            {{ item.title }}
          </v-btn>
        </nav>
        
        <!-- Mobile Menu Toggle -->
        <v-btn
          v-show="$vuetify.display.mdAndDown"
          icon
          class="menu-toggle"
          @click="toggleMobileMenu"
        >
          <v-icon>mdi-menu</v-icon>
        </v-btn>
      </div>
    </header>

    <!-- Mobile Menu -->
    <v-navigation-drawer
      v-model="mobileMenuOpen"
      temporary
      location="top"
      class="mobile-menu-drawer"
      :style="{ top: '80px' }"
    >
      <v-list class="mobile-menu-list">
        <v-list-item
          v-for="item in menuItems"
          :key="item.title"
          @click="handleMobileMenuClick(item.section)"
          class="mobile-menu-item"
        >
          <v-list-item-title class="mobile-menu-title">
            {{ item.title }}
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <!-- Hero Section -->
    <section class="hero-section" id="hero">
      <div class="hero-image-container">
        <img src="@/assets/images/big-kitchen.jpg" alt="급식소" class="hero-main-image" />
        <div class="hero-overlay">
          <div class="hero-content">
            <div class="hero-text-box">
              <h1 class="hero-main-title text-center">요양시설 위탁급식</h1>
              <h3 class="hero-highlight text-center">경영 혁신과 경쟁력 강화를 위한<br>프리미엄 위탁급식 솔루션</h3>
              <div class="hero-company-info">
                <p class="company-name">주식회사 함우리</p>
                <p class="company-tagline">요양시설 급식 전문 파트너</p>
              </div>
              <div class="hero-actions">
                <v-btn
                  size="large"
                  color="primary"
                  class="inquiry-btn"
                  @click="scrollToSection('inquiry')"
                  prepend-icon="mdi-send"
                >
                  무료 상담신청하기
                </v-btn>
                <div class="contact-info">
                  <p class="contact-label">급식 문의전화</p>
                  <p class="contact-number">1833-7489</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- About Section -->
    <section class="section about-section" id="about">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">제안 개요</h2>
          <h3 class="section-subtitle">급식, 이제는 전략이 필요하다</h3>
        </div>
        <div class="about-content">
          <div class="about-text">
            
            <div class="about-points">
              <h4 class="about-main-title">
              복잡한 급식 운영으로부터<br>
              <span class="highlight">완전한 해방</span>을 제안합니다.
            </h4>
              <div class="point-item">
                <v-icon class="point-icon" color="primary">mdi-target</v-icon>
                <div class="point-text">
                  <h5>어르신 케어라는 본질에 집중</h5>
                  <p>경영 효율성은 높이는 전략적 선택</p>
                </div>
              </div>
              <div class="point-item">
                <v-icon class="point-icon" color="primary">mdi-heart</v-icon>
                <div class="point-text">
                  <h5>어르신의 건강과 행복</h5>
                  <p>그리고 시설의 지속 성장</p>
                </div>
              </div>
            </div>
          </div>
          <div class="about-image">
            <img src="@/assets/images/smile_grand_mother2.png" alt="상담" />
          </div>
        </div>
      </div>
    </section>

    <!-- Problems Section -->
    <section class="section problems-section" id="menu">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">직영급식의 문제점</h2>
          <h3 class="section-subtitle">회계/인력/법적/위생 관리의 삼중고</h3>
        </div>
        <div class="problems-grid">
          <div class="problem-card smooth-hover">
            <v-icon class="problem-icon" color="error">mdi-calculator</v-icon>
            <h4>복잡한 회계 처리</h4>
            <p>복잡한 회계 처리, 인력 관리의 어려움,<br/> 날로 강화되는 법적·위생 규제로<br/> 시설장님의 어깨는 더욱 무거워지고 있습니다.</p>
          </div>
          <div class="problem-card smooth-hover">
            <v-icon class="problem-icon" color="warning">mdi-alert-circle</v-icon>
            <h4>잉여금처리의 투명성 이슈</h4>
            <p>식재료비 수입에서 발생하는 <br/>잉여금 처리 방식의 불명확성으로 <br/>회계 감사와 평가에서 <br/>취약점으로 지적될 수 있습니다.</p>
          </div>
          <div class="problem-card smooth-hover">
            <v-icon class="problem-icon" color="error">mdi-shield-alert</v-icon>
            <h4>가중되는 행정 리스크</h4>
            <p>집단급식소 법적 규제 위반 시 <br/> 최대 1,000만원 과태료, 영업정지, <br/>식중독 발생 시 민형사 책임까지 <br/>시설장님께 부담이 되는 운영</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Why Now Section -->
    <section class="section why-now-section" id="hygiene">
      <div class="container">
        <div class="why-now-content">
          <div class="why-now-text">
            <h2 class="section-title">위탁급식, 변화가 필요한 이유</h2>
            <h3 class="section-subtitle">지금이 전환의 적기입니다</h3>
            <div class="why-now-points">
              <p class="main-point">전문가 집단과의 <span class="highlight">파트너십</span>이 새로운 경쟁력입니다.</p>
              <p class="sub-point">복잡한 급식 운영은 함우리에게 맡기고 어르신 케어에 집중하는 현명한 선택</p>
              <p class="sub-point">시설 운영의 복잡성과 위험 관리의 한계</p>
            </div>
          </div>
          <div class="why-now-image">
            <img src="@/assets/images/papers.png" alt="기술 혁신" />
          </div>
        </div>
      </div>
    </section>

    <!-- Core Reasons Section -->
    <section class="section core-reasons-section" id="management">
      <div class="core-reasons-background">
        <img src="@/assets/images/meals_place.png" alt="프리미엄 식재료" class="core-reasons-bg-image" />
      </div>
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">위탁급식 도입의 8가지 핵심 이유</h2>
        </div>
        <div class="reasons-grid">
          <!-- Reason 1 -->
          <div class="reason-card smooth-hover">
            <div class="reason-number">01</div>
            <h4 class="reason-title">재무적 효율성</h4>
            <div class="reason-content">
              <h5>한 장의 세금계산서로 100% 비용처리</h5>
              <p>복잡한 식재료비, 인건비, 운영비가 아닌 단일 위탁급식비로 <br/>간결하고 투명한 회계 처리가 가능합니다.</p>
              <ul>
                <li>잉여금문제, 회계 처리 부담 해소</li>
                <li>예측 가능한 재무 관리로 경영 안정</li>
              </ul>
            </div>
          </div>

          <!-- Reason 2 -->
          <div class="reason-card smooth-hover">
            <div class="reason-number">02</div>
            <h4 class="reason-title">법적/행정 리스크 완전 해소</h4>
            <div class="reason-content">
              <h5>법적 기준 100% 준수</h5>
              <p>집단급식소 설치신고, 보존식 관리, 위생관리 등 <br/>모든 법적 기준에 대한 완벽한 컴플라이언스를 보장합니다.</p>
              <ul>
                <li>위생관리/행정서류 전문가 전담</li>
                <li>법적 책임에서 완전 보호</li>
              </ul>
            </div>
          </div>

          <!-- Reason 3 -->
          <div class="reason-card smooth-hover">
            <div class="reason-number">03</div>
            <h4 class="reason-title">입소자 만족도 극대화</h4>
            <div class="reason-content">
              <h5>전문 영양사 맞춤식단 제공</h5>
              <p>국가공인 영양사가 작성한 영양학적으로 완벽한 균형식으로 <br/>어르신의 건강 상태를 고려한 맞춤형 식단을 제공합니다.</p>
              <ul>
                <li>질환별/연령별 및 연하곤란 식단 차별화</li>
                <li>보호자 신뢰도 상승과 입소 경쟁력 강화</li>
              </ul>
            </div>
          </div>

          <!-- Reason 4 -->
          <div class="reason-card smooth-hover">
            <div class="reason-number">04</div>
            <h4 class="reason-title">프리미엄 식재료 공급</h4>
            <div class="reason-content">
              <h5>삼성웰스토리와의 프리미엄 공급망</h5>
              <p>국내 최대 식자재 유통사와의 전략적 파트너십으로 <br/>안정적인 식재료 수급과 품질 보증이 가능합니다.</p>
              <ul>
                <li>신선하고 안전한 식재료</li>
                <li>합리적인 비용의 프리미엄 식자재</li>
              </ul>
            </div>
          </div>

          <!-- Reason 5 -->
          <div class="reason-card smooth-hover">
            <div class="reason-number">05</div>
            <h4 class="reason-title">본업 집중 환경 조성</h4>
            <div class="reason-content">
              <h5>식자재/인력/위생 업무에서 완전 해방</h5>
              <p>복잡한 급식 관련 업무의 부담에서 벗어나 시설 운영의 핵심 가치에 <br/>더 많은 시간과 에너지를 투자하세요.</p>
              <ul>
                <li>어르신 케어라는 본질적 업무에 집중 가능</li>
                <li>업무 효율성과 시간 관리의 혁신</li>
              </ul>
            </div>
          </div>

          <!-- Reason 6 -->
          <div class="reason-card smooth-hover">
            <div class="reason-number">06</div>
            <h4 class="reason-title">전문가 철저 대응 시스템</h4>
            <div class="reason-content">
              <h5>설치신고, 행정서류, 위생 등 전문가 철저 대응</h5>
              <p>집단급식소 관련 모든 법적 의무와 행정 요건을 전문가가 <br/>체계적으로 관리합니다.</p>
              <ul>
                <li>집단급식소 설치신고 및 변경신고 대행</li>
                <li>위생관리 및 행정서류 완벽 관리</li>
              </ul>
            </div>
          </div>

          <!-- Reason 7 -->
          <div class="reason-card smooth-hover">
            <div class="reason-number">07</div>
            <h4 class="reason-title">기관 이미지 제고</h4>
            <div class="reason-content">
              <h5>식사 품격과 기관 이미지 제고, 신뢰성 확보</h5>
              <p>프리미엄 식사 제공으로 입소자 만족도와 기관 평판이 향상되며 <br/>지속가능한 운영 시스템을 구축합니다.</p>
              <ul>
                <li>고품질 식사로 기관 브랜드 가치 향상</li>
                <li>보호자 신뢰도 증대 및 입소 경쟁력 강화</li>
              </ul>
            </div>
          </div>

          <!-- Reason 8 -->
          <div class="reason-card smooth-hover">
            <div class="reason-number">08</div>
            <h4 class="reason-title">운영 위험 완전 이전</h4>
            <div class="reason-content">
              <h5>식중독, 민형사 책임 등 운영 위험 완전 이전</h5>
              <p>급식 관련 모든 법적 책임과 운영 리스크를 전문업체로 이전하여 <br/>위험 부담을 최소화합니다.</p>
              <ul>
                <li>식중독 발생 시 민형사 책임 면제</li>
                <li>컴플라이언스 리스크 완전 해소</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Partnership Section -->
    <section class="section partnership-section" id="contact">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">파트너십 제안과 다음 단계</h2>
          <h3 class="section-subtitle">미래를 위한 전략적 선택</h3>
          <p class="partnership-quote">"함우리와 함께 요양시설의 미래경쟁력을 준비하십시오."</p>
          <p class="partnership-desc">급식 운영의 모든 부담에서 벗어나 어르신 케어의 본질에 집중하는 선택</p>
        </div>
        
        <div class="process-steps">
          <div class="step-item">
            <div class="step-number">1</div>
            <div class="step-content">
              <h4>무료 현장 컨설팅 및 맞춤제안</h4>
              <p>현재 상황을 정확히 파악하고 최적의<br/>솔루션을 제안합니다</p>
            </div>
          </div>
          <div class="step-arrow">
            <v-icon color="primary">mdi-arrow-right</v-icon>
          </div>
          <div class="step-item">
            <div class="step-number">2</div>
            <div class="step-content">
              <h4>계약 및 서비스 설계</h4>
              <p>시설 특성에 맞는 서비스 설계와<br/>계약을 진행합니다</p>
            </div>
          </div>
          <div class="step-arrow">
            <v-icon color="primary">mdi-arrow-right</v-icon>
          </div>
          <div class="step-item">
            <div class="step-number">3</div>
            <div class="step-content">
              <h4>급식 서비스 및 지속적 관리</h4>
              <p>전문적인 급식 서비스 제공과<br/>지속적인 품질 관리</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 상담신청 섹션 -->
    <section class="section inquiry-section" id="inquiry">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">상담신청</h2>
          <h3 class="section-subtitle">전문가와 함께 시작하세요</h3>
          <p class="inquiry-desc">시설에 맞는 최적의 급식 솔루션을 제안해드립니다</p>
        </div>
        
        <div class="inquiry-content">
          <InquiryFormEmbed />
        </div>
      </div>
    </section>

    
<footer class="footer-section">
      <v-container>
        <div class="footer-content">
          <v-row class="footer-main">
            <!-- 회사 정보 -->
            <v-col cols="12" md="4" class="footer-column">
              <div class="footer-brand">
                <h3 class="footer-logo">장기요양지원센터</h3>
                <p class="footer-tagline">위탁급식</p>
              </div>
              
              <div class="footer-company-info">
                <h4 class="footer-section-title">회사 정보</h4>
                <div class="company-details">
                  <p><strong>(주)함우리</strong></p>
                  <p>사업자등록번호: 185-81-02287</p>
                </div>
              </div>
            </v-col>
            
            <!-- 연락처 정보 -->
            <v-col cols="12" md="4" class="footer-column">
              <h4 class="footer-section-title">연락처</h4>
              <div class="contact-info">
                <div class="contact-item">
                  <v-icon icon="mdi-phone" size="18" class="contact-icon"></v-icon>
                  <div class="contact-details">
                    <p class="contact-label">전화번호</p>
                    <p class="contact-value">1833-7489</p>
                  </div>
                </div>
                
                <div class="contact-item">
                  <v-icon icon="mdi-email" size="18" class="contact-icon"></v-icon>
                  <div class="contact-details">
                    <p class="contact-label">이메일</p>
                    <p class="contact-value">hamwoori25@naver.com</p>
                  </div>
                </div>
                
                <div class="contact-item">
                  <v-icon icon="mdi-web" size="18" class="contact-icon"></v-icon>
                  <div class="contact-details">
                    <p class="contact-label">웹사이트</p>
                    <p class="contact-value">https://장기요양지원센터.kr</p>
                  </div>
                </div>
              </div>
            </v-col>
            
            <!-- 주소 정보 -->
            <v-col cols="12" md="4" class="footer-column">
              <h4 class="footer-section-title">사업장 주소</h4>
              <div class="address-info">
                <div class="address-item">
                  <v-icon icon="mdi-map-marker" size="18" class="address-icon"></v-icon>
                  <div class="address-details">
                    <p class="address-label">서울 사무실</p>
                    <p class="address-value">서울특별시 송파구 법원로90 파트너스2</p>
                  </div>
                </div>
                
                <div class="address-item">
                  <v-icon icon="mdi-map-marker" size="18" class="address-icon"></v-icon>
                  <div class="address-details">
                    <p class="address-label">전북 사무실</p>
                    <p class="address-value">전북특별자치도 전주시 덕진구 기지로77</p>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
          
          <!-- 푸터 하단 -->
          <div class="footer-bottom">
            <v-divider class="footer-divider"></v-divider>
            <v-row align="center" class="footer-bottom-content">
              <v-col cols="12" md="6">
                <p class="copyright">©2025 HAMWOORI Corp. All rights reserved.</p>
              </v-col>
              <v-col cols="12" md="6" class="text-md-end">
                <div class="footer-links">
                  <a href="#" @click.prevent="scrollToTop" class="footer-link">맨위로</a>
                </div>
              </v-col>
            </v-row>
          </div>
        </div>
      </v-container>
    </footer>
    <!-- Floating Talk Button -->
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { useStore } from 'vuex';
import InquiryFormEmbed from '@/components/inquiry/InquiryFormEmbed.vue';

const store = useStore();
const isScrolled = ref(false);
const mobileMenuOpen = ref(false);

// Menu items array for better management
const menuItems = [
  { title: '제안 개요', section: 'about' },
  { title: '직영급식 문제점', section: 'menu' },
  { title: '변화 필요성', section: 'hygiene' },
  { title: '8가지 핵심 이유', section: 'management' },
  { title: '파트너십 제안', section: 'contact' },
  { title: '상담신청', section: 'inquiry' }
];

const handleScroll = () => {
  isScrolled.value = window.scrollY > 50;
};

// Methods
const scrollToSection = (sectionId: string) => {
  const element = document.getElementById(sectionId)
  if (element) {
    const headerHeight = 80 // 고정된 헤더 높이
    const elementPosition = element.offsetTop - headerHeight
    
    window.scrollTo({
      top: elementPosition,
      behavior: 'smooth'
    })
  }
}

const handleMobileMenuClick = (sectionId: string) => {
  scrollToSection(sectionId)
  mobileMenuOpen.value = false
}

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
};

const handleContactClick = () => {
  store.commit('showSnackbar', {
    text: "상담신청이 접수되었습니다. 곧 연락드리겠습니다!",
    color: "success"
  });
};

const handleTalkClick = () => {
  store.commit('showSnackbar', {
    text: "카카오톡 상담으로 연결됩니다.",
    color: "info"
  });
  // 실제로는 카카오톡 상담 링크로 이동
};

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
  handleScroll();
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

const gotoMain = () => {
  window.location.href = 'https://xn--ok0b647ab9cu3avi17b59gjrt.kr';
}
</script>

<style lang="scss">
@use '@/assets/styles/indexPage.scss';
</style>